import { Breadcrumb } from '@components/breadcrumb';
import { DetailFilter } from '@components/detailFilter';
import { LiveFilter } from '@components/liveFilter';
import * as moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useFragment } from 'react-relay';
import 'moment/locale/de';
import type { ILocationDescriptor } from 'src/container/FundsService';
import { PublicationsYearGroup, dateFormat, filterCol, filterColByDetailField, publicationsPageFragment } from './lib';
import type { PublicationDocumentWithYear } from './lib/types';
import type { GroupedDocuments, PublicationsPageProps } from './types';

export const PublicationsPage: React.FC<PublicationsPageProps> = (props) => {
  const page = useFragment(publicationsPageFragment, props.page);

  const publicationsList = page.publicationsList;
  const documents = publicationsList?.documents;
  const hasPublications = documents != null && documents.length > 0;
  const isin = useMemo(() => {
    const params = new URLSearchParams(document.location.search);
    return params.get('isin');
  }, []);

  // ---------------------------------------------------------------------------
  // Filter state (matching ArchiveList pattern)
  // ---------------------------------------------------------------------------
  const [liveFilterInfo, setLiveFilterInfo] = useState<{ [key: string]: string[] }>(() => {
    if (isin) {
      return { text: [isin], header: [isin] };
    }
    return {};
  });
  const [detailFilterInfo, setDetailFilterInfo] = useState<{ [key: string]: { key: string; filterVals: string[] } }>({});
  const [detailFilterVisible, setDetailFilterVisible] = useState(false);

  // ---------------------------------------------------------------------------
  // Filter callbacks (matching ArchiveList pattern)
  // ---------------------------------------------------------------------------
  const liveFilterHandler = useCallback((cols: Array<string>, p: any): void => {
    const filterInfo = cols.reduce(
      (acc, col) => {
        acc[col] = [p.target.value];
        return acc;
      },
      {} as { [key: string]: string[] },
    );

    setLiveFilterInfo(filterInfo);
  }, []);

  const detailFilterHandler = useCallback((col: string, filterVals: Array<string>, key: string): void => {
    if (!col) {
      setDetailFilterInfo({});
    } else {
      setDetailFilterInfo((prev) => {
        const newFilterInfo = { ...prev };

        if (filterVals.length === 0 || filterVals.indexOf('all') > -1) {
          delete newFilterInfo[col];
        } else {
          newFilterInfo[col] = {
            key,
            filterVals,
          };
        }

        return newFilterInfo;
      });
    }
  }, []);

  const toggleDetailFilter = useCallback((e: any): void => {
    setDetailFilterVisible((prev) => !prev);
  }, []);

  // ---------------------------------------------------------------------------
  // Language detection (matching ArchiveList pattern)
  // ---------------------------------------------------------------------------
  const language = useMemo(() => {
    let lang = 'de';
    const loc = props.location as ILocationDescriptor | string;
    if (loc) {
      if (typeof loc === 'string' && loc.substring(0, 3) === 'en/') {
        lang = 'en';
      } else if (typeof loc !== 'string' && loc.pathname?.substring(0, 4) === '/en/') {
        lang = 'en';
      }
    }
    if (props.location.state?.language) {
      lang = props.location.state.language.toLowerCase();
    }
    return lang;
  }, [props.location]);

  moment.locale(language);

  // ---------------------------------------------------------------------------
  // Filtering pipeline and grouping
  // ---------------------------------------------------------------------------
  const { filteredDocuments, groupedByYear, sortedYears } = useMemo(() => {
    if (!documents) {
      return { filteredDocuments: [] as PublicationDocumentWithYear[], groupedByYear: {} as GroupedDocuments, sortedYears: [] as string[] };
    }

    // 1. Enrich documents with parsed year/month and field aliases
    const today = moment().format(dateFormat); // 'YYYY-MM-DD'
    const enriched: PublicationDocumentWithYear[] = documents
      .filter((doc): doc is NonNullable<typeof doc> => doc != null)
      // Exclude documents published in the future
      .filter((doc) => !doc.fileDate || doc.fileDate <= today)
      .map((doc) => ({
        ...doc,
        year: moment(doc.fileDate, dateFormat).format('YYYY'),
        month: Number(moment(doc.fileDate, dateFormat).format('M')),
        // Alias so server-sent col:'doctypeheader' resolves to the right field
        doctypeheader: doc.docTypeName,
      }));

    // 2. Apply live filter (text search across mapped fields)
    const afterLiveFilter = enriched.filter((doc) => filterCol(doc, liveFilterInfo));

    // 3. Apply detail filters (iterate over all active detail filter keys)
    const afterDetailFilter = Object.keys(detailFilterInfo).reduce((docs, fieldKey) => docs.filter((doc) => filterColByDetailField(doc, detailFilterInfo, fieldKey)), afterLiveFilter);

    // 4. Group by year
    const grouped = afterDetailFilter.reduce<GroupedDocuments>((acc, doc) => {
      if (!acc[doc.year]) {
        acc[doc.year] = [];
      }
      acc[doc.year].push(doc);
      return acc;
    }, {});

    // 5. Sort years descending
    // const test = [...Object.keys(grouped), '2024', '2025'].sort((a, b) => Number(b) - Number(a));
    const years = Object.keys(grouped).sort((a, b) => Number(b) - Number(a));

    // console.log('TTT', test, years);

    return { filteredDocuments: afterDetailFilter, groupedByYear: grouped, sortedYears: years };
  }, [documents, liveFilterInfo, detailFilterInfo]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div>
      <Breadcrumb data={page.breadcrumbs} />
      <div className="Widget-container Base-container relative mx-auto flex w-full flex-wrap items-start justify-between max-w-[calc(100vw-40px)] tablet:max-w-[565px] tablet:w-[calc(100vw-80px)] desktop:max-w-[820px]">
        {/* Page title — matching ArchiveList archiveTitle style */}
        <h2 className="Base-title mb-10 w-full text-[32px] leading-10 tablet:text-[40px] tablet:leading-[1.1]">{publicationsList?.title}</h2>

        {hasPublications ? (
          <>
            {/* Filter section — matching ArchiveList layout */}
            <div className="mt-0 w-full max-[620px]:mt-5">
              {/* Text search + toggle row */}
              <div className="flex flex-col w-full flex-wrap items-center justify-between border-b border-[#d0d0d0] pb-5 gap-2 tablet:flex-row">
                {publicationsList?.liveFilter && (
                  <LiveFilter
                    liveFilter={publicationsList.liveFilter}
                    filterHandlerFn={liveFilterHandler}
                    filterText={Object.values(liveFilterInfo)[0]?.[0] || ''}
                    filterCols={['text', 'header']}
                    className="min-w-[34%] w-full tablet:w-auto flex-1 rounded-[20px] border border-[#d0d0d0] bg-[url(/static/front/assets/other-icons/search.png)] bg-position-[right_8px_center] bg-no-repeat px-[15px] py-[5px] pr-[35px] text-sm focus-within:border-[#a0a0a0] focus-within:outline-none max-[620px]:mb-2.5"
                  />
                )}
                <button type="button" onClick={toggleDetailFilter} className="relative z-10 cursor-pointer pr-[22px] text-left w-full tablet:w-auto text-sm text-accent no-underline">
                  {detailFilterVisible ? publicationsList?.filterButtonTextExpanded : publicationsList?.filterButtonText}
                  <span
                    aria-hidden={true}
                    className={`absolute right-0 top-0 h-5 w-[25px] bg-[url(/static/front/assets/chevron/_chevron-down.svg)] bg-position-[right_5px_center] bg-no-repeat transition-transform max-[620px]:relative max-[620px]:inline-block ${detailFilterVisible ? 'rotate-180' : ''}`}
                  />
                </button>
              </div>
              {publicationsList?.detailFilter && (
                <DetailFilter
                  isVisible={detailFilterVisible}
                  detailFilter={publicationsList.detailFilter}
                  filterHandlerFn={detailFilterHandler}
                  resetHandlerFn={detailFilterHandler}
                  filterVals={detailFilterInfo}
                  filteredCounter={filteredDocuments.length}
                  hasResetOption={false}
                  radio={true}
                />
              )}
            </div>

            {/* Grouped document list */}
            <div className="mt-[50px] w-full">
              {sortedYears.length > 0 ? (
                sortedYears.map((year, index) => (
                  <PublicationsYearGroup
                    key={year}
                    documents={groupedByYear[year]}
                    yearLabel={publicationsList?.yearLabel || ''}
                    year={year}
                    isFirst={index === 0}
                    language={language}
                    variousFundsText={publicationsList.variousFundsText}
                  />
                ))
              ) : (
                <div className="my-4 mb-10 font-bold text-[#3b3b3b]">{publicationsList?.noResultsText}</div>
              )}
            </div>
          </>
        ) : (
          /* Empty state — no publications from server */
          <div className="my-4 mb-10 font-bold text-[#3b3b3b]">{publicationsList?.noResultsText}</div>
        )}
      </div>
    </div>
  );
};
